import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { PageHeader, AdminBody, Card } from "../../components/admin/Bits";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, Area, AreaChart, BarChart, Bar, CartesianGrid } from "recharts";
import { Users, FileText, Receipt, IndianRupee, ArrowUpRight } from "lucide-react";

export default function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/dashboard/stats").then(r => setS(r.data)); }, []);

  if (!s) return <div className="p-10 text-navy-900/50">Loading royal dashboard…</div>;

  const tiles = [
    { label: "New Leads", value: s.leads_new, total: s.leads_total, icon: Users, color: "from-gold/15 to-transparent" },
    { label: "Quotations", value: s.quotations_total, icon: FileText, color: "from-navy-900/10 to-transparent" },
    { label: "Invoices", value: s.invoices_total, icon: Receipt, color: "from-gold/15 to-transparent" },
    { label: "Revenue", value: `₹${(s.revenue_total || 0).toLocaleString("en-IN")}`, icon: IndianRupee, color: "from-navy-900/10 to-transparent" },
  ];

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Realtime overview of leads, quotations & invoices." />
      <AdminBody>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {tiles.map((t, i) => (
            <Card key={i} className={`p-6 bg-gradient-to-br ${t.color} relative`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="label-eyebrow">{t.label}</div>
                  <div className="serif text-4xl mt-2">{t.value}</div>
                  {t.total !== undefined && <div className="text-navy-900/50 text-xs mt-1">of {t.total} total</div>}
                </div>
                <div className="w-12 h-12 rounded-full bg-navy-900 flex items-center justify-center">
                  <t.icon size={20} className="text-gold" />
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-5 mt-6">
          <Card className="lg:col-span-2 p-6">
            <div className="label-eyebrow mb-4">Leads · Last 14 days</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={s.leads_series}>
                  <defs>
                    <linearGradient id="gold" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(10,17,40,0.08)" />
                  <XAxis dataKey="date" stroke="#0A1128" fontSize={10} />
                  <YAxis stroke="#0A1128" fontSize={10} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#0A1128", border: "1px solid #D4AF37", color: "#FAF9F6", fontSize: 12 }} />
                  <Area type="monotone" dataKey="leads" stroke="#D4AF37" strokeWidth={2} fill="url(#gold)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-6">
            <div className="label-eyebrow mb-4">Package Interest</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={s.package_interest}>
                  <CartesianGrid stroke="rgba(10,17,40,0.08)" />
                  <XAxis dataKey="name" stroke="#0A1128" fontSize={10} />
                  <YAxis stroke="#0A1128" fontSize={10} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#0A1128", border: "1px solid #D4AF37", color: "#FAF9F6", fontSize: 12 }} />
                  <Bar dataKey="count" fill="#D4AF37" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        <Card className="mt-6 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="label-eyebrow">Latest Inquiries</div>
            <a href="/admin/leads" className="text-gold text-[11px] tracking-[0.2em] uppercase flex items-center gap-1 hover:gap-2 transition-all" data-testid="dashboard-all-leads">All <ArrowUpRight size={12} /></a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ivory-200 text-navy-900">
                <tr>{["Name", "Phone", "Event", "Package", "Date"].map(h => <th key={h} className="text-left p-3 text-[10px] tracking-[0.2em] uppercase">{h}</th>)}</tr>
              </thead>
              <tbody>
                {s.latest_leads.map(l => (
                  <tr key={l.id} className="border-t border-navy-900/8">
                    <td className="p-3 font-semibold">{l.name}</td>
                    <td className="p-3 text-navy-900/50">{l.phone}</td>
                    <td className="p-3 text-navy-900/50">{l.event_type || "—"}</td>
                    <td className="p-3 text-gold">{l.package || "—"}</td>
                    <td className="p-3 text-navy-900/50 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {s.latest_leads.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-navy-900/50">No leads yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </AdminBody>
    </>
  );
}
